/**
 * Badge component for displaying subscription status
 */

import { BadgeWithDot } from '@/lib/base/badges/badges';
import type { SubscriptionStatus } from '@/lib/billing/types';

interface SubscriptionStatusBadgeProps {
  status: SubscriptionStatus;
  cancelAtPeriodEnd?: boolean;
  className?: string;
}

const STATUS_CONFIG: Record<
  SubscriptionStatus,
  { color: 'success' | 'warning' | 'error' | 'gray' | 'brand'; label: string }
> = {
  active: { color: 'success', label: 'Active' },
  trialing: { color: 'brand', label: 'Trial' },
  past_due: { color: 'warning', label: 'Past Due' },
  unpaid: { color: 'error', label: 'Unpaid' },
  canceled: { color: 'gray', label: 'Cancelled' },
  incomplete: { color: 'warning', label: 'Incomplete' },
  incomplete_expired: { color: 'error', label: 'Expired' },
};

export function SubscriptionStatusBadge({
  status,
  cancelAtPeriodEnd,
  className,
}: SubscriptionStatusBadgeProps) {
  // Show "Expiring soon" for active subscriptions set to cancel
  if (status === 'active' && cancelAtPeriodEnd) {
    return (
      <BadgeWithDot
        type='pill-color'
        size='sm'
        color='warning'
        className={className}
      >
        Expiring soon
      </BadgeWithDot>
    );
  }

  const config = STATUS_CONFIG[status] || { color: 'gray', label: status };

  return (
    <BadgeWithDot
      type='pill-color'
      size='sm'
      color={config.color}
      className={className}
    >
      {config.label}
    </BadgeWithDot>
  );
}
